import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { api } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Search, IndianRupee, Calendar, User, AlertTriangle, CheckCircle2, Loader2, ArrowRight } from 'lucide-react';
import { formatCurrency, formatDate } from '@/utils/helpers';

function Collections() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('due_this_month'); // 'all', 'due_this_month', 'overdue_only'

  useEffect(() => {
    fetchMembers();
  }, [filterType]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const data = await api.collections.getMembers(filterType, searchQuery);
      setMembers(data);
    } catch (error) {
      console.error('Error fetching collections:', error);
      toast.error('Failed to load collections');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchMembers();
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Filter members by search query (client-side for instant feedback)
  const filteredMembers = members.filter((member) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      member.member_name.toLowerCase().includes(query) ||
      (member.account_number && member.account_number.toLowerCase().includes(query)) ||
      (member.loan_account_number && member.loan_account_number.toLowerCase().includes(query)) ||
      (member.contact_number && member.contact_number.includes(query))
    );
  });

  // Calculate stats
  const stats = {
    totalMembers: filteredMembers.length,
    totalDue: filteredMembers.reduce((sum, m) => sum + m.total_due_this_month, 0),
    overdueCount: filteredMembers.filter(m => m.is_overdue).length,
  };

  return (
    <DashboardLayout title="Collections">
      <div className="space-y-6 fade-in pt-2">
        {/* Header with Stats - Consistent padding */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="shadow-sm">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Members</p>
                  <p className="text-lg md:text-2xl font-bold">{stats.totalMembers}</p>
                </div>
                <User className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Due</p>
                  <p className="text-lg md:text-2xl font-bold">{formatCurrency(stats.totalDue)}</p>
                </div>
                <IndianRupee className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className={`shadow-sm ${stats.overdueCount > 0 ? 'border-red-500' : ''}`}>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Overdue Members</p>
                  <p className="text-lg md:text-2xl font-bold text-red-600">{stats.overdueCount}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters and Search */}
        <Card className="shadow-sm">
          <CardContent className="p-4 md:p-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Filter Buttons */}
              <div className="flex gap-2">
                <Button
                  variant={filterType === 'due_this_month' ? 'default' : 'outline'}
                  onClick={() => setFilterType('due_this_month')}
                  size="sm"
                >
                  Due This Month
                </Button>
                <Button
                  variant={filterType === 'overdue_only' ? 'default' : 'outline'}
                  onClick={() => setFilterType('overdue_only')}
                  size="sm"
                >
                  Overdue Only
                </Button>
                <Button
                  variant={filterType === 'all' ? 'default' : 'outline'}
                  onClick={() => setFilterType('all')}
                  size="sm"
                >
                  All Active
                </Button>
              </div>

              {/* Search */}
              <div className="flex-1 flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, account number, phone..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={handleSearchKeyPress}
                    className="pl-10"
                  />
                </div>
                <Button onClick={handleSearch} variant="secondary">
                  Search
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Members List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2">Loading collections...</span>
          </div>
        ) : filteredMembers.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <CheckCircle2 className="h-12 w-12 text-green-500 mb-4" />
              <p className="text-lg font-semibold">No Members Found</p>
              <p className="text-sm text-muted-foreground mt-1">
                {filterType === 'due_this_month' && 'No members with dues this month'}
                {filterType === 'overdue_only' && 'No overdue payments'}
                {filterType === 'all' && 'No active accounts'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredMembers.map((member) => (
              <Card key={member.member_id} className={member.is_overdue ? 'border-l-4 border-l-red-500' : ''}>
                <CardContent className="pt-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Member Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="text-lg font-semibold truncate" title={member.member_name}>{member.member_name}</h3>
                        {member.is_overdue && (
                          <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-full whitespace-nowrap">
                            Overdue
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground space-y-1">
                        <p className="truncate" title={`Account: ${member.account_number}`}>Account: {member.account_number}</p>
                        {member.contact_number && <p className="truncate" title={`Phone: ${member.contact_number}`}>Phone: {member.contact_number}</p>}
                      </div>
                    </div>

                    {/* Loan Info */}
                    {member.loan_id && (
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">EMI Loan</span>
                          <span className="text-sm text-muted-foreground">{member.loan_account_number}</span>
                        </div>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">EMI Amount:</span>
                            <span className="font-medium">{formatCurrency(member.emi_amount)}</span>
                          </div>
                          {member.next_emi_due_date && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Next Due:</span>
                              <span className={member.is_overdue ? 'text-red-600 font-medium' : ''}>
                                {formatDate(member.next_emi_due_date)}
                              </span>
                            </div>
                          )}
                          {member.overdue_count > 0 && (
                            <div className="flex justify-between text-red-600">
                              <span>Overdue EMIs:</span>
                              <span className="font-semibold">
                                {member.overdue_count} ({formatCurrency(member.total_overdue)})
                              </span>
                            </div>
                          )}
                          {member.last_payment_date && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Last Paid:</span>
                              <span className="text-xs">{formatDate(member.last_payment_date)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Lavajam Info */}
                    {member.lavajam_account_id && (
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Lavajam</span>
                          <span className="text-sm text-muted-foreground">{member.lavajam_account_number}</span>
                        </div>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Monthly:</span>
                            <span className="font-medium">{formatCurrency(member.lavajam_monthly_amount)}</span>
                          </div>
                          {member.lavajam_next_due_date && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Next Due:</span>
                              <span>{formatDate(member.lavajam_next_due_date)}</span>
                            </div>
                          )}
                          {member.lavajam_pending_months > 0 && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Pending:</span>
                              <span className="font-medium">
                                {member.lavajam_pending_months} months
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Total Due & Action */}
                    <div className="flex flex-col items-end gap-3 min-w-0 sm:min-w-[200px]">
                      {member.total_due_this_month > 0 && (
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground mb-1">Total Due This Month</p>
                          <p className="text-lg md:text-2xl font-bold text-primary">
                            {formatCurrency(member.total_due_this_month)}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {member.emi_amount > 0 && `EMI: ${formatCurrency(member.emi_amount)}`}
                            {member.emi_amount > 0 && member.lavajam_monthly_amount > 0 && ' | '}
                            {member.lavajam_monthly_amount > 0 && `Lavajam: ${formatCurrency(member.lavajam_monthly_amount)}`}
                          </p>
                        </div>
                      )}

                      <Link to={`/manager/members/${member.member_id}`}>
                        <Button variant="default" className="w-full">
                          View Profile
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default Collections;
